// MaturaArithGeoSignsClosedTask.js
// Zadanie maturalne – zamknięte (ciąg arytmetyczny + geometryczny, znaki x i y)
// Trzywyrazowy ciąg jest arytmetyczny, drugi – geometryczny. Na końcu sprawdzamy znaki x, y.

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";

const LETTERS = ["A", "B", "C", "D"];

const randInt = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

const pick = (arr) => arr[randInt(0, arr.length - 1)];

// ======================= GENERATOR =======================

function createOriginalTask() {
  const options = [
    { label: "A", latex: "x < 0 \\text{ i } y < 0", key: "x<0,y<0" },
    { label: "B", latex: "x < 0 \\text{ i } y > 0", key: "x<0,y>0" },
    { label: "C", latex: "x > 0 \\text{ i } y < 0", key: "x>0,y<0" },
    { label: "D", latex: "x > 0 \\text{ i } y > 0", key: "x>0,y>0" },
  ].map((option, index) => ({
    ...option,
    index,
    isCorrect: index === 1,
  }));

  return {
    type: "MaturaArithGeoSignsClosedTask",
    data: {
      k: 5,
      rootValue: Math.sqrt(5),
      rootLatex: "\\sqrt{5}",
      xValue: 2 - Math.sqrt(5),
      yValue: 1 / Math.sqrt(5),
      xPositive: false,
      yPositive: true,
      xPosition: "third",
      yPosition: "third",
      options,
      correctIndex: 1,
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_SIE",
      taskNumber: 18,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") {
    return createOriginalTask();
  }

  // wybieramy k tak, aby sqrt(k) > 1 i != 2 (żeby x != 0)
  const K_VALUES = [2, 3, 5, 6, 7, 8, 10];
  const k = pick(K_VALUES);
  const rootValue = Math.sqrt(k);
  const rootLatex = `\\sqrt{${k}}`;

  // pozycje x i y: pierwszy lub trzeci wyraz ciągu
  const xPosition = pick(["first", "third"]); // w ciągu arytm.
  const yPosition = pick(["first", "third"]); // w ciągu geom.

  // wartości x i y z warunków
  const xValue = 2 - rootValue;
  const yValue = 1 / rootValue;

  const xPositive = xValue > 0;
  const yPositive = yValue > 0; // zawsze true dla rootValue > 0

  // poprawna kombinacja znaków
  // (przy naszym generatorze zawsze: y > 0)
  const correctKey = xPositive ? "x>0,y>0" : "x<0,y>0";

  const baseOptions = [
    {
      label: "A",
      latex: "x < 0 \\text{ i } y < 0",
      key: "x<0,y<0",
    },
    {
      label: "B",
      latex: "x < 0 \\text{ i } y > 0",
      key: "x<0,y>0",
    },
    {
      label: "C",
      latex: "x > 0 \\text{ i } y < 0",
      key: "x>0,y<0",
    },
    {
      label: "D",
      latex: "x > 0 \\text{ i } y > 0",
      key: "x>0,y>0",
    },
  ];

  const options = baseOptions.map((opt, idx) => ({
    ...opt,
    index: idx,
  }));

  const correctIndex = options.findIndex((o) => o.key === correctKey);

  return {
    type: "MaturaArithGeoSignsClosedTask",
    data: {
      k,
      rootValue,
      rootLatex,
      xValue,
      yValue,
      xPositive,
      yPositive,
      xPosition,
      yPosition,
      options,
      correctIndex,
    },
  };
}

// pomocniczo – do wypisywania trójek (x, 1, sqrt{k}) / (sqrt{k}, 1, x)
function buildTripleLatex(rootLatex, variableSymbol, position) {
  const first = position === "first" ? variableSymbol : rootLatex;
  const third = position === "first" ? rootLatex : variableSymbol;
  return `(${first},\\,1,\\,${third})`;
}

// ======================= RENDER TREŚCI =======================

export function renderTask({ data }) {
  const { rootLatex, xPosition, yPosition, options } = data;

  const tripleArith = buildTripleLatex(rootLatex, "x", xPosition);
  const tripleGeo = buildTripleLatex(rootLatex, "y", yPosition);

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Zadanie maturalne – zamknięte.</strong>
      </p>
      <p>
        Trzywyrazowy ciąg{" "}
        <InlineMath math={tripleArith} /> jest arytmetyczny.
      </p>
      <p>
        Trzywyrazowy ciąg{" "}
        <InlineMath math={tripleGeo} /> jest geometryczny.
      </p>
      <p>Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.</p>
      <p>
        Liczby <InlineMath math={'x'} /> oraz{" "}
        <InlineMath math={'y'} /> spełniają warunki:
      </p>
      <div style={{ marginTop: 8 }}>
        {options.map((opt) => (
          <div key={opt.label} style={{ marginBottom: 10 }}>
            <InlineMath math={`${opt.label}.\\; ${opt.latex}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ======================= INPUT (ABCD) =======================

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const { options } = data;

  const choiceIndex =
    userAnswer && typeof userAnswer.choiceIndex === "number"
      ? userAnswer.choiceIndex
      : null;

  const letterOptions = options.map((opt, idx) => ({
    value: idx,
    label: opt.label,
    textNode: opt.label,
  }));

  const handleChange = (idx) => {
    const opt = options[idx];
    onChange?.({
      choiceIndex: idx,
      label: opt.label,
      latex: opt.latex,
      key: opt.key,
    });
  };

  return (
    <ABCDInput
      options={letterOptions}
      value={choiceIndex}
      onChange={handleChange}
      disabled={disabled}
    />
  );
}

// ======================= SPRAWDZANIE =======================

export function checkAnswer({ data }, userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !Number.isInteger(userAnswer.choiceIndex)
  ) {
    return false;
  }
  return userAnswer.choiceIndex === data.correctIndex;
}

// ======================= POPRAWNA ODPOWIEDŹ =======================

export function renderAnswer({ data }) {
  const { options, correctIndex } = data;
  const correct = options[correctIndex];

  return <InlineMath math={`${correct.label}.\\; ${correct.latex}`} />;
}

// ======================= ODPOWIEDŹ UŻYTKOWNIKA =======================

export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !userAnswer.label ||
    userAnswer.latex == null
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  const { label, latex } = userAnswer;

  return (
    <div style={{ marginTop: "1rem" }}>
      <span>Twoja odpowiedź: </span>
      <InlineMath math={`${label}.\\; ${latex}`} />
    </div>
  );
}

// ======================= WYJAŚNIENIE =======================

export function renderExplanation({ data }) {
  const { rootLatex, xValue, yValue, xPositive } = data;

  const stepArith1 =
    "a_n = \\dfrac{a_{n-1} + a_{n+1}}{2}";
  const stepArith2 =
    `1 = \\dfrac{${rootLatex} + x}{2}`;
  const stepArith3 =
    `2 = ${rootLatex} + x`;
  const stepArith4 =
    `x = 2 - ${rootLatex}`;

  const stepGeo1 =
    "(a_n)^2 = a_{n-1}\\cdot a_{n+1}";
  const stepGeo2 =
    `1^2 = ${rootLatex}\\cdot y`;
  const stepGeo3 =
    `1 = ${rootLatex}\\cdot y`;
  const stepGeo4 =
    `y = \\dfrac{1}{${rootLatex}}`;

  const signXText = xPositive ? "x > 0" : "x < 0";
  const signYText = "y > 0";

  const summaryLatex = `${signXText}\\ \\text{ oraz }\\ ${signYText}`;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Krok 1.</strong> Korzystamy ze wzoru na środkowy wyraz
        trzywyrazowego ciągu arytmetycznego{" "}
        <InlineMath math={'(a_n)'} />:
      </p>
      <BlockMath math={stepArith1} />
      <p>
        W naszym zadaniu środkowym wyrazem jest{" "}
        <InlineMath math={'1'} />, a pozostałe to{" "}
        <InlineMath math={rootLatex} /> oraz{" "}
        <InlineMath math={'x'} />, więc:
      </p>
      <BlockMath math={stepArith2} />
      <BlockMath math={stepArith3} />
      <BlockMath math={stepArith4} />
      <p>
        Ponieważ <InlineMath math={`${rootLatex}${xPositive ? "<" : ">"}2`} />, to wartość{" "}
        <InlineMath math={'x = 2 - ' + rootLatex} /> jest{" "}
        {xPositive ? "dodatnia" : "ujemna"}. W szczególności{" "}
        <InlineMath math={xPositive ? 'x > 0' : 'x < 0'} />.
      </p>

      <p>
        <strong>Krok 2.</strong> Dla trzywyrazowego ciągu geometrycznego{" "}
        <InlineMath math={'(a_n)'} /> korzystamy ze wzoru:
      </p>
      <BlockMath math={stepGeo1} />
      <p>
        Środkowym wyrazem jest znów{" "}
        <InlineMath math={'1'} />, a dwa pozostałe
        to <InlineMath math={rootLatex} /> oraz{" "}
        <InlineMath math={'y'} />:
      </p>
      <BlockMath math={stepGeo2} />
      <BlockMath math={stepGeo3} />
      <BlockMath math={stepGeo4} />
      <p>
        Liczba <InlineMath math={rootLatex} /> jest dodatnia, więc też{" "}
        <InlineMath math={'y = 1/' + rootLatex} /> jest dodatnia, czyli{" "}
        <InlineMath math={'y > 0'} />.
      </p>

      <p>
        <strong>Wniosek.</strong> Ostatecznie:
      </p>
      <BlockMath math={summaryLatex} />
    </div>
  );
}
